import { Injectable } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, map, mergeMap, of } from "rxjs";
import * as CategoryActions from "./category.actions";
import { CategoryService } from "app/modules/masters/categories/categories.service";

@Injectable()
export class CategoryEffects {
  constructor(
    private actions$: Actions,
    private categoryService: CategoryService
  ) {}

  /** Prefer API body message over raw Angular HttpClient text. */
  private extractErrorMessage(error: unknown, fallback = "Something went wrong"): string {
    if (error instanceof HttpErrorResponse) {
      const body = error.error;
      const apiMessage = body?.message;
      if (Array.isArray(apiMessage) && apiMessage.length > 0) {
        return String(apiMessage[0]);
      }
      if (typeof apiMessage === "string" && apiMessage.trim()) {
        return apiMessage.trim();
      }
      if (typeof body === "string" && body.trim()) {
        return body.trim();
      }
      if (error.status === 409) {
        return "Category with this name already exists";
      }
      return fallback;
    }
    if (error && typeof error === "object" && "message" in error) {
      const message = String((error as { message?: unknown }).message ?? "").trim();
      if (message && !message.startsWith("Http failure response")) {
        return message;
      }
    }
    return fallback;
  }

  // Load all categories
  loadCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoryActions.loadCategories),
      mergeMap(() =>
        this.categoryService.getCategories().pipe(
          map((categories) =>
            CategoryActions.loadCategoriesSuccess({ categories:categories.data })
          ),
          catchError((error) =>
            of(
              CategoryActions.loadCategoriesFailure({
                error: this.extractErrorMessage(error, "Failed to load categories"),
              })
            )
          )
        )
      )
    )
  );

  // Load single category
  loadCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoryActions.loadCategory),
      mergeMap(({ id }) =>
        this.categoryService.getCategory(id).pipe(
          map((category) => CategoryActions.loadCategorySuccess({ category })),
          catchError((error) =>
            of(
              CategoryActions.loadCategoryFailure({
                error: this.extractErrorMessage(error, "Failed to load category"),
              })
            )
          )
        )
      )
    )
  );

  // Create category
  createCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoryActions.createCategory),
      mergeMap(({ category }) =>
        this.categoryService.createCategory(category).pipe(
          map((createdCategory) =>
            CategoryActions.createCategorySuccess({ category: createdCategory })
          ),
          catchError((error) =>
            of(
              CategoryActions.createCategoryFailure({
                error: this.extractErrorMessage(
                  error,
                  "Failed to create category",
                ),
              })
            )
          )
        )
      )
    )
  );

  // Reload categories after successful creation
  createCategorySuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoryActions.createCategorySuccess),
      map(() => CategoryActions.loadCategories())
    )
  );

  // Update category
  updateCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoryActions.updateCategory),
      mergeMap(({ id, category }) =>
        this.categoryService.updateCategory(id, category).pipe(
          map((updatedCategory) =>
            CategoryActions.updateCategorySuccess({ category: updatedCategory })
          ),
          catchError((error) =>
            of(
              CategoryActions.updateCategoryFailure({
                error: this.extractErrorMessage(
                  error,
                  "Failed to update category",
                ),
              })
            )
          )
        )
      )
    )
  );

  // Delete category
  deleteCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoryActions.deleteCategory),
      mergeMap(({ id, permanent }) =>
        this.categoryService.deleteCategory(id, permanent).pipe(
          map(() => CategoryActions.deleteCategorySuccess({ id })),
          catchError((error) =>
            of(
              CategoryActions.deleteCategoryFailure({
                error: this.extractErrorMessage(
                  error,
                  "Failed to delete category",
                ),
              })
            )
          )
        )
      )
    )
  );

  // Reload categories after successful deletion
  deleteCategorySuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoryActions.deleteCategorySuccess),
      map(() => CategoryActions.loadCategories())
    )
  );

  checkCategoryName$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoryActions.checkCategoryName),
      mergeMap(({ name, categoryType, excludeId }) =>
        this.categoryService.checkName(name, categoryType, excludeId).pipe(
          map((res) =>
            CategoryActions.checkCategoryNameSuccess({ exists: res.exists })
          ),
          catchError((error) =>
            of(
              CategoryActions.checkCategoryNameFailure({
                error: this.extractErrorMessage(
                  error,
                  "Failed to check category name",
                ),
              })
            )
          )
        )
      )
    )
  );

  // Check for duplicate category code
  checkCategoryCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoryActions.checkCategoryCode),
      mergeMap(({ code, excludeId }) =>
        this.categoryService.checkCode(code, excludeId).pipe(
          map((res) =>
            CategoryActions.checkCategoryCodeSuccess({ exists: res.exists })
          ),
          catchError((error) =>
            of(
              CategoryActions.checkCategoryCodeFailure({
                error: this.extractErrorMessage(
                  error,
                  "Failed to check category code",
                ),
              })
            )
          )
        )
      )
    )
  );
}
